import { isApp as isAppIndex } from '@/index'
import { CANVAS_CREATE_METHOD, SHAPES, CANVAS_HANDLE_MODE } from './const'
import { CanvasEngineEntity } from './entity'
import { CanvasEngineLayerController } from './layerController'
import type {
  CanvasEngineExportEntityType,
  ICanvasEngineEntityController,
  ShapeCreateConfigs,
  Vector2d,
} from './types'
import { getRandomString } from '@/lib'
import { BaseEntityProperties } from './entity/base/baseEntityProperties'
import type { ICircle, INode, ISelectionHandles, ITicker } from './backend/IRenderer'
import type { TickerFrame } from './backend/types'
import { useAllTaskStore } from '@/app/store/task'
import { storeToRefs } from 'pinia'

// 비활성화 직전의 노드 상태 백업 attr (setDisabledShapes ↔ clearDisabledShapes 대칭 복구용)
const DISABLED_STATE_BACKUP_ATTR = '__disabledStateBackup'

interface DisabledStateBackup {
  listening: boolean
  draggable: boolean
}

/** Konva.Easings.EaseInOut(t, 0, 1, 1)과 같은 식(2차 ease-in-out) — 포커스 이동 애니메이션 */
function easeInOut(t: number): number {
  if ((t /= 0.5) < 1) return 0.5 * t * t
  return -0.5 * (--t * (t - 2) - 1)
}

export class CanvasEngineEntityController
  extends CanvasEngineLayerController
  implements ICanvasEngineEntityController
{
  // 선택 핸들러가 만든 선택 핸들·라인 핸들. 엔진은 해제(destroy)만 한다
  protected __lineTransformerStartHandle: ICircle | undefined
  protected __lineTransformerEndHandle: ICircle | undefined
  protected __shapeTransformer: ISelectionHandles | undefined
  protected __entityInteractionMode: boolean = true
  // 비활성화된 entity ID 목록
  private disabledEntityIds: Set<string> = new Set()
  // 색상필터 등으로 숨겨진 entity ID 목록
  private hiddenEntityIds: Set<string> = new Set()
  // 현재 진행 중인 애니메이션
  private currentAnimation: ITicker | null = null
  private currentAnimationResolve: ((value: boolean) => void) | null = null
  // 선택 작업의 버전 카운터 (stale한 focusAndSelect 방지)
  private __selectionVersion: number = 0

  refreshKey = ''

  initLineTransformerStartHandle(handle: ICircle): void {
    if (this.__lineTransformerStartHandle) {
      this.__lineTransformerStartHandle.destroy()
    }
    this.__lineTransformerStartHandle = handle
  }
  initLineTransformerEndHandle(handle: ICircle): void {
    if (this.__lineTransformerEndHandle) {
      this.__lineTransformerEndHandle.destroy()
    }
    this.__lineTransformerEndHandle = handle
  }
  initShapeTransformer(transformer: ISelectionHandles): void {
    if (this.__shapeTransformer) {
      this.__shapeTransformer.destroy()
    }
    this.__shapeTransformer = transformer
  }

  // NO_SHAPE 결함이 차지하는 번호를 설정
  setReservedIndices(reserved: Set<number>): void {
    this.reservedIndices = reserved
  }

  // 미등록 결함 연결 시 다음 생성 엔티티에 표시할 번호 지정
  setPendingCreateBalloonText(text: string | null): void {
    this.pendingCreateBalloonText = text
  }

  useIndex(shapeId: string): string {
    // 예약된 번호(NO_SHAPE가 차지) 건너뛰기
    while (this.reservedIndices.has(this.index)) {
      this.index++
    }
    const _index = (this.index++).toString()
    this.indexMap[shapeId] = _index
    this.events.onIndexChanged(this.indexMap)
    return _index
  }

  /**
   * @param isRefreshCache 인덱스 업데이트 후 캐시 새로고침 여부
   */
  async updateIndex(isRefreshCache: boolean = false): Promise<void> {
    // 번호 변경 판정용 이전 인덱스 맵 보존
    const prevIndexMap = this.indexMap
    this.index = 1
    if (this.isAllFloorNumbering) {
      this.index = this.allFloorIndex
    }
    this.indexMap = {}

    for (const entity of this.entities) {
      entity.applyIndex()
    }

    // 캐시 갱신 없이 번호만 바꾸면 캐시 비트맵이 낡으므로 다음 refreshCache가 전량 갱신하도록 표시.
    // 변경분 재캐싱 모드는 아래에서 직접 갱신하므로 플래그를 건드리지 않는다
    // (await 뒤에 되돌리면 재캐싱 중 발생한 변형 알림을 덮어쓸 수 있음)
    if (!isRefreshCache) this.isEntityTransformed = true
    // 항상 즉시 렌더 (캐시 유무와 무관)
    this.renderer.layers.background.batchDraw()

    if (isRefreshCache) {
      // 번호가 실제로 바뀐(추가/변경/제거) 엔티티만 재캐싱 — 전량 재캐싱 방지
      const changedIds = new Set<string>()
      for (const [shapeId, indexValue] of Object.entries(this.indexMap)) {
        if (prevIndexMap[shapeId] !== indexValue) changedIds.add(shapeId)
      }
      for (const shapeId of Object.keys(prevIndexMap)) {
        if (!(shapeId in this.indexMap)) changedIds.add(shapeId)
      }
      await this.refreshAsyncCache(changedIds)
    }
    // 재계산된 전체 인덱스 맵을 전달 (기존에는 빈 객체가 전달되던 버그)
    this.events.onIndexChanged(this.indexMap)
  }

  getEntities(): CanvasEngineEntity[] {
    return this.entities
  }

  /**
   * 스테이지 좌표(컨테이너 CSS px)에서 포인터 이벤트가 닿을 엔티티. 브러시 기하 hit와 Konva hit를 합친
   * 판정과 같다. 없거나 엔티티 밖 노드(선택 핸들 등)면 null
   */
  pickEntityAt(stagePoint: Vector2d): CanvasEngineEntity | null {
    let node: INode | null = this.renderer.hitTest(stagePoint)
    if (!node) return null
    const byRoot = new Map<INode, CanvasEngineEntity>(
      this.entities.map(entity => [entity.node, entity]),
    )
    while (node) {
      const entity = byRoot.get(node)
      if (entity) return entity
      node = node.getParent()
    }
    return null
  }

  /**
   * 엔티티를 정렬한 후 index를 업데이트 함.
   * @param shapeIds
   * @returns
   */
  sortEntities(shapeIds: string[]): void {
    // id → entity 맵 1회 구성 (getEntityById 반복 호출의 O(n²) 제거, 첫 매칭 우선 유지)
    const entityById = new Map<string, CanvasEngineEntity>()
    for (const entity of this.entities) {
      const entityId = entity.getId()
      if (!entityById.has(entityId)) entityById.set(entityId, entity)
    }
    const shapeIdsSet = new Set(shapeIds)

    // 모든 엔티티를 순회하면서 표시/숨김 처리
    // hiddenEntityIds(색상 필터 숨김)는 setHiddenEntityIds로 해제되기 전까지 항상 우선한다
    this.entities.forEach((entity: CanvasEngineEntity) => {
      const entityId = entity.getId()
      if (shapeIdsSet.has(entityId) && !this.hiddenEntityIds.has(entityId)) {
        entity.node.show()
      } else if (!entityId.startsWith('created_')) {
        // 생성 중인 엔티티(임시 ID)는 숨기지 않음 — ID 확정 전 syncCanvas 경쟁 방지
        entity.node.hide()
      }
    })

    // shapeIds 순서의 엔티티 목록 (인스턴스 기준 중복 제거)
    const ordered: CanvasEngineEntity[] = []
    const moved = new Set<CanvasEngineEntity>()
    for (const id of shapeIds) {
      const entity = entityById.get(id)
      if (entity && !moved.has(entity)) {
        moved.add(entity)
        ordered.push(entity)
      }
    }

    if (!ordered.length) {
      this.renderer.layers.background.batchDraw()
      return
    }

    // [ordered…, 나머지(기존 상대 순서 유지)] — 기존 findIndex+splice 루프와 동일한 결과를 O(n)으로 생성
    const rest = this.entities.filter((entity: CanvasEngineEntity) => !moved.has(entity))
    this.entities = [...ordered, ...rest]
    this.backgroundImage?.moveToBottom()
    this.renderer.layers.background.batchDraw()
    // 인덱스 업데이트 + 캐시 갱신 (번호 변경을 캐시된 엔티티에도 반영)
    this.updateIndex(true)
  }

  getShapeToCenterCoordinate(shape: BaseEntityProperties): Vector2d {
    const view = this.renderer.stage
    const x = shape.node.x() * view.scaleX()
    const y = shape.node.y() * view.scaleY()
    return {
      x: view.width() / 2 - x,
      y: view.height() / 2 - y,
    }
  }

  selectedShapeBlur(): void {
    if (this.selectedEntity) {
      this.selectedEntity.blur()
      this.moveEntityToLayer(this.selectedEntity, this.selectedEntity.getLayerId())
      this.selectedEntity = undefined
    }
  }

  selectEntityById(shapeId: string): void {
    this.selectedShapeId = shapeId
    if (!shapeId) return
    const shape = this.getEntityById(shapeId)
    if (!shape) {
      throw new Error('도형을 찾을 수 없습니다.')
    }

    this.selectShape(shape)
  }
  selectEntityByIdRelated(shapeId: string): void {
    this.selectedShapeId = shapeId
    if (!shapeId) return
    const shape = this.getEntityById(shapeId)
    if (!shape) {
      throw new Error('도형을 찾을 수 없습니다.')
    }
  }

  selectShape(entity: CanvasEngineEntity, originalShapeId?: string): void {
    // 자동 정렬 모드일때는 선택불가
    if (this.canvasHandleMode == CANVAS_HANDLE_MODE.AUTO_ALIGN) return
    // 여러번 클릭시 간혹 선택되는 경우를 방지
    if (this.canvasHandleMode == CANVAS_HANDLE_MODE.DRAW) return
    // listening이 false거나 비활성화된 entity는 선택 불가
    if (
      !entity.node.listening() ||
      this.disabledEntityIds.has(entity.getId()) ||
      this.hiddenEntityIds.has(entity.getId())
    )
      return

    // 이벤트에 전파할 shapeId 결정
    // 자식 선택시에 캔버스에서는 부모를 선택하고 이벤트전파는 자식(원본)으로함
    const eventShapeId = originalShapeId || entity.getId()

    this.renderer.hints.unmarkStatic(entity.node)
    if (entity === this.selectedEntity) {
      this.events.onSelectShape(eventShapeId)
      return
    }

    if (this.selectedEntity) {
      this.moveEntityToLayer(this.selectedEntity, this.selectedEntity.getLayerId())
      this.selectedEntity.blur()
    }

    // originalShapeId가 있으면 그것을 사용, 없으면 entity ID 사용
    this.selectedShapeId = eventShapeId

    this.selectedEntity = entity
    this.renderer.layers.system.add(this.entityNode(entity))

    this.events.onSelectShape(eventShapeId)

    const selectionVersion = ++this.__selectionVersion

    // 뷰포트 이동 애니메이션 완료 후 Transformer 생성 (shape과 transformer 분리 방지)
    const focusAndSelect = async (): Promise<void> => {
      try {
        if (!this.isMobile) {
          await this.focusCanvasToShape(entity)
        } else {
          await this.focusCanvasToShapeForApp(entity)
        }
      } catch (error) {
        console.error('[CanvasEngine] focusAndSelect animation error:', error)
        this.isAnimating = false
      }

      // 애니메이션 완료 후 select 호출 (Transformer 생성)
      if (this.selectedEntity === entity && this.__selectionVersion === selectionVersion) {
        try {
          this.selectedEntity.select()
        } catch (selectError) {
          console.error('[CanvasEngine] select error:', selectError)
        }
      }

      if (this.autoModeChangeWhenShapeSelected) {
        this.changeCanvasHandleMode(CANVAS_HANDLE_MODE.DEFAULT)
      }

      setTimeout(() => {
        this.events.onViewportChanged({
          x: this.renderer.stage.x(),
          y: this.renderer.stage.y(),
          zoom: this.renderer.stage.scaleX(),
        })
      }, 300)
    }

    focusAndSelect().catch(err => {
      console.error('[CanvasEngine] Unhandled focusAndSelect error:', err)
      this.isAnimating = false
    })
  }

  onEntityTransform(shape: BaseEntityProperties | string): void {
    if (typeof shape === 'string') {
      this.events.onEntityTransform(shape)
    } else {
      this.events.onEntityTransform(shape.getId())
    }
  }

  onTransformShapes(shapes: BaseEntityProperties[] | string[]): void {
    const ids = shapes.map(shape => (typeof shape === 'string' ? shape : shape.getId()))
    this.events.onTransformShapes(ids)
  }

  getEntityById(shapeId: string): CanvasEngineEntity | undefined {
    if (!shapeId) {
      return undefined
    }
    return this.entities.find((item: CanvasEngineEntity) => item.getId() == shapeId)
  }

  removeAllEntities(ignoreBrush: boolean = false): void {
    for (const entity of this.entities) {
      if (ignoreBrush && entity.isBrush()) continue
      entity.node.destroy()
    }
    this.entities = []
    this.renderer.requestDraw()
  }

  removeEntity(shapeId: string): void {
    const shape = this.getEntityById(shapeId)
    if (!shape) {
      throw new Error('찾을 수 없는 도형입니다.')
    }
    // 선택된 엔티티를 지울 때는 먼저 blur 한다 — blurAllEntity 가 moveEntityToLayer 로 선택 엔티티를
    // 레이어에 다시 add 하므로 destroy 뒤에 호출하면 파괴된 그룹이 레이어에 유령으로 남는다
    this.blurAllEntity()
    shape.node.destroy()
    this.entities = this.entities.filter((item: CanvasEngineEntity) => item.getId() != shapeId)
    const entityIds = this.entities.map((item: CanvasEngineEntity) => item.getId())
    this.sortEntities(entityIds)
    this.renderer.requestDraw()
  }
  removeEntityByIds(shapeIds: string[]): void {
    this.entities = this.entities.filter(
      (item: CanvasEngineEntity) => !shapeIds.includes(item.getId()),
    )
    const entityIds = this.entities.map((item: CanvasEngineEntity) => item.getId())
    this.sortEntities(entityIds)
    this.renderer.requestDraw()
  }

  getEntityLength(): number {
    return this.entities.filter((x: CanvasEngineEntity) => {
      if (x.isBrush()) return false
      if (x.isBackgroundEntity) return false

      return true
    }).length
  }

  blurSelectedEntity(): void {
    if (this.selectedEntity) {
      this.selectedEntity.removeShapeAnimation()
      this.selectedEntity = undefined
    }
    this.events.onLostFocus()

    if (this.__lineTransformerEndHandle) {
      this.__lineTransformerEndHandle.destroy()
    }
    if (this.__lineTransformerStartHandle) {
      this.__lineTransformerStartHandle.destroy()
    }
    if (this.__shapeTransformer) {
      this.__shapeTransformer.destroy()
    }

    this.restoreEntitiyListening()
  }

  blurAllEntity(): void {
    if (this.selectedEntity) {
      this.selectedEntity.blur()
      this.moveEntityToLayer(this.selectedEntity, this.selectedEntity.getLayerId())
      this.selectedEntity = undefined
    }
    this.events.onLostFocus()

    if (this.__lineTransformerEndHandle) {
      this.__lineTransformerEndHandle.destroy()
    }
    if (this.__lineTransformerStartHandle) {
      this.__lineTransformerStartHandle.destroy()
    }
    if (this.__shapeTransformer) {
      this.__shapeTransformer.destroy()
    }

    this.restoreEntitiyListening()
  }

  // 기존 항목을 추가할 때 사용
  // Ballon 까지.
  // Balloon 은 전역적으로 opacity를 0 주거나 하는 방법 사용.
  // Shape이 무엇인지, 어디 위치에, balloon 위치는 어디인지, rotate는 어느정도인지. color은 무슨 색인지.
  moveEntityToLayer(shape: CanvasEngineEntity, layerId: string): void {
    const beforeLayerId = shape.getLayerId()
    const destinationLayerId = layerId
    this.renderer.layers.background.add(this.entityNode(shape))

    if (beforeLayerId === destinationLayerId) return

    this.entityLayerMap[beforeLayerId] = this.entityLayerMap[beforeLayerId].filter(
      (item: string) => item !== shape.getId(),
    )
    this.entityLayerMap[destinationLayerId].push(shape.getId())
  }

  //CanvasEngineExportShapeType
  importEntity(_data: CanvasEngineExportEntityType): CanvasEngineEntity {
    return CanvasEngineEntity.importFromExportData(this, _data)
  }
  //CanvasEngineExportShapeType == disabled
  importEntityWithInteractionMode(
    _data: CanvasEngineExportEntityType,
    interactionMode: boolean = true,
  ): CanvasEngineEntity {
    return CanvasEngineEntity.importFromExportData(this, _data, interactionMode)
  }

  importEntityToCurrentIndex(
    _data: CanvasEngineExportEntityType,
    currentIndex?: number,
  ): CanvasEngineEntity {
    const addedEntity = CanvasEngineEntity.importFromExportData(this, _data)
    const findEntity = this.entities.find(
      (entity: CanvasEngineEntity) => entity.getId() === addedEntity.getId(),
    )
    if (findEntity) {
      // 배열 제일 끝에 있는 요소를 currentIndex 위치로 이동 — 0(맨 앞)도 유효한 위치, 없거나 음수면 끝
      const targetIndex =
        currentIndex != null && currentIndex >= 0 ? currentIndex : this.entities.length
      this.entities.splice(targetIndex, 0, findEntity)
      this.entities.pop()
    }
    return addedEntity
  }

  updateEntityLayerId(shapeId: string, layerId: string): void {
    const shape = this.getEntityById(shapeId)
    if (!shape) {
      throw new Error('찾을 수 없는 도형입니다.')
    }
    shape.moveToLayer(layerId)
  }

  exportEntity(shapeId: string): CanvasEngineExportEntityType {
    const shape = this.getEntityById(shapeId)
    if (!shape) {
      throw new Error('찾을 수 없는 도형입니다.')
    }
    return shape.toExportData()
  }

  exportEntities(): CanvasEngineExportEntityType[] {
    const output: CanvasEngineExportEntityType[] = []
    for (const entity of this.entities) {
      output.push(entity.toExportData())
    }
    return output
  }

  /**
   * 이벤트 리스너 'onCreateShape' 을 호출하는 함수.
   * @param shape
   */
  onCreatedEntity(shape: CanvasEngineEntity): void {
    // 전차 등록 모드는 인덱스 비활성화 (admin_index로 번호풍선 처리함)
    const { isPrevRegistMode } = storeToRefs(useAllTaskStore())
    if (isPrevRegistMode.value) {
      shape.disableIndex()
    } else if (this.pendingCreateBalloonText !== null) {
      // 미등록 결함 연결: 지정된 번호를 즉시 표시하고 updateIndex 생략
      shape.enableIndex()
      shape.setBalloonAdminText(this.pendingCreateBalloonText)
      this.pendingCreateBalloonText = null
    } else {
      shape.enableIndex()
      this.updateIndex()
    }
    this.__isCreateCallbackPending = true
    // 실제 할당되는 콜백은 async이므로 Promise를 반환할 수 있음
    const result = this.events.onCreateShape(shape) as unknown
    if (result && typeof (result as Promise<void>).then === 'function') {
      ;(result as Promise<void>)
        .catch(err => console.error('onCreateShape callback error:', err))
        .finally(() => {
          this.__isCreateCallbackPending = false
        })
    } else {
      this.__isCreateCallbackPending = false
    }
  }

  onCreatedBrush(shape: CanvasEngineEntity): void {
    this.__isCreateCallbackPending = true
    const result = this.events.onCreateBrush(shape) as unknown
    if (result && typeof (result as Promise<void>).then === 'function') {
      ;(result as Promise<void>)
        .catch(err => console.error('onCreateBrush callback error:', err))
        .finally(() => {
          this.__isCreateCallbackPending = false
        })
    } else {
      this.__isCreateCallbackPending = false
    }
  }

  changeSelectedEntityColor(color: string): void {
    if (!this.selectedEntity) {
      throw new Error('도형을 찾을 수 없습니다.')
    }
    this.selectedEntity.changeColor(color)
    const { shapeType, isFill } = this.selectedEntity.getShapeStyleAttrs()
    this.selectedEntity.setShapeStyleToggle(shapeType)
    this.selectedEntity.setShapeFillToggle(isFill)
  }

  changeSelectedEntityColorForPreview(shapeId: string, color: string): void {
    const entity = this.getEntityById(shapeId)
    if (!entity) {
      throw new Error('도형을 찾을 수 없습니다.')
    }
    entity.changeColor(color)
    const { shapeType, isFill } = entity.getShapeStyleAttrs()
    entity.setShapeStyleToggle(shapeType)
    entity.setShapeFillToggle(isFill)
  }

  generateShapeId(): string {
    return `created_${getRandomString(12, false)}`
  }

  getCreateEntityType(): SHAPES {
    return this.selectedCreateEntityType
  }

  setCreateEntityType(mode: SHAPES): void {
    this.selectedCreateEntityType = mode
  }

  getBasicCreateConfig(
    _type?: keyof ShapeCreateConfigs,
  ): ShapeCreateConfigs[keyof ShapeCreateConfigs] {
    const type = _type ?? this.getCreateEntityType()
    const mode = this.getCreateMode()

    switch (mode) {
      case CANVAS_CREATE_METHOD.DRAG_AND_DROP:
        switch (type) {
          case SHAPES.POINT:
            return {
              radius: 3,
              color: this.forCreateBalloonBorderColor,
            }
          case SHAPES.RECT:
            return {
              width: this.options.basicCreationWidth,
              height: this.options.basicCreationHeight,
              color: this.forCreateBalloonBorderColor,
            }
          case SHAPES.ELLIPSE:
            return {
              width: this.options.basicCreationWidth,
              height: this.options.basicCreationHeight,
              color: this.forCreateBalloonBorderColor,
            }
          case SHAPES.ARROW:
          case SHAPES.LINE:
            return {
              color: this.forCreateBalloonBorderColor,
            }
          // 방향도형
          case SHAPES.DIRECTION:
            return {
              color: this.forCreateBalloonBorderColor,
            } as ShapeCreateConfigs[SHAPES.DIRECTION]
          case SHAPES.DOUBLE_ARROW:
            return {
              points: [90, 0, 100, 0], // 화살표의 시작점과 끝점
              pointerLength: 10,
              pointerWidth: 10,
              color: this.forCreateBalloonBorderColor,
            } as ShapeCreateConfigs[SHAPES.DOUBLE_ARROW]
          case SHAPES.BRUSH:
            return {
              color: this.drawingColor,
              width: this.drawingThickness,
            }
          case SHAPES.TRIPOD:
            return {
              color: this.forCreateBalloonBorderColor,
              tripodShapePoints: {
                mainAxisPoint1: [0, 0],
                mainAxisPoint2: [0, 0],
                dots: [],
                targetIds: [],
                mainShapeId: '',
                tailStyle: 'dot',
              },
            }
        }
      case CANVAS_CREATE_METHOD.MOUSE_UP:
      case CANVAS_CREATE_METHOD.DRAG:
        switch (type) {
          case SHAPES.POINT:
            return {
              radius: 3,
              color: this.forCreateBalloonBorderColor,
            }
          case SHAPES.RECT:
            return {
              width: 20,
              height: 20,
              color: this.forCreateBalloonBorderColor,
            }
          case SHAPES.ELLIPSE:
            return {
              width: 20,
              height: 20,
              color: this.forCreateBalloonBorderColor,
            }
          case SHAPES.ARROW:
          case SHAPES.LINE:
            return {
              color: this.forCreateBalloonBorderColor,
            }
          case SHAPES.DIRECTION:
            return {
              color: this.forCreateBalloonBorderColor,
            } as ShapeCreateConfigs[SHAPES.DIRECTION]
          case SHAPES.DOUBLE_ARROW:
            return {
              points: [85, 0, 105, 0], // 화살표의 시작점과 끝점
              pointerLength: 10,
              pointerWidth: 10,
              color: this.forCreateBalloonBorderColor,
            } as ShapeCreateConfigs[SHAPES.DOUBLE_ARROW]
          case SHAPES.BRUSH:
            return {
              color: this.drawingColor,
              width: this.drawingThickness,
            }
          case SHAPES.TRIPOD:
            return {
              color: this.forCreateBalloonBorderColor,
              tripodShapePoints: {
                mainAxisPoint1: [0, 0],
                mainAxisPoint2: [0, 0],
                dots: [],
                targetIds: [],
                mainShapeId: '',
                tailStyle: 'dot',
              },
            }
        }
    }
  }

  protected isApp = isAppIndex()
  protected async focusCanvasToShape(shape: CanvasEngineEntity): Promise<boolean> {
    // 중심점 계산
    const center = shape.getFullBoundaryCenter()

    // 스테이지 정보
    const stageWidth = this.renderer.stage.width()
    const stageHeight = this.renderer.stage.height()
    const targetZoom = center.zoom

    // 화면 비율(aspect ratio) 계산
    const aspectRatio = stageWidth / stageHeight

    // 화면 중앙 좌표 (타겟 줌 기준으로 계산)
    const centerScreenX = stageWidth / 2 / targetZoom
    const centerScreenY = stageHeight / 2 / targetZoom

    // 앱과 웹 환경에 따른 기본 오프셋 비율 설정
    let baseOffsetRatio = 0.15 // 웹에서의 기본값

    if (this.isApp) {
      // 앱 환경에서는 더 작은 오프셋(약간만 왼쪽으로)
      baseOffsetRatio = 0.05
    }

    // 화면 비율에 따른 오프셋 조정 (가로가 넓을수록 더 많이 오프셋)
    const aspectAdjustedOffset =
      aspectRatio < 1
        ? baseOffsetRatio * Math.max(aspectRatio, 0.5) // 세로형 화면에서 오프셋 줄임
        : baseOffsetRatio // 가로형 화면에서 기본 오프셋 유지

    // 브러시 여부에 따라 오프셋 계산
    const isBrush = shape.entityType === SHAPES.BRUSH

    // 오프셋 계산 (타겟 줌 기준)
    const horizontalOffset = (stageWidth * aspectAdjustedOffset) / targetZoom

    // 브러시인 경우 오프셋 조정 (더 정확한 중앙 포커스를 위해)
    let targetCenterX = centerScreenX - horizontalOffset

    // 브러시의 경우 중앙에 더 가깝게 위치시킴
    if (isBrush) {
      targetCenterX = centerScreenX - horizontalOffset * 0.5 // 브러시는 오프셋을 절반만 적용
    }

    // 수직 위치는 항상 중앙
    const targetCenterY = centerScreenY

    // 최종 타겟 좌표 계산 (타겟 줌 기준으로 통일)
    const targetX = (targetCenterX - center.x) * targetZoom
    const targetY = (targetCenterY - center.y) * targetZoom

    return await this.animatePosition(targetX, targetY, targetZoom)
  }

  /**
   * App 환경에서 shape를 화면 중앙으로 포커스하는 함수
   * - 가로: 화면 정중앙
   * - 세로: 화면 정중앙
   * @param {CanvasEngineEntity} shape - 포커스할 shape
   * @returns {Promise<boolean>} - 애니메이션 완료 여부
   */
  protected async focusCanvasToShapeForApp(shape: CanvasEngineEntity): Promise<boolean> {
    // 중심점 계산
    const center = shape.getFullBoundaryCenter()

    // 스테이지 정보
    const stageWidth = this.renderer.stage.width()

    // shape의 권장 zoom 값 사용 (shape 크기에 따라 자동 계산됨)
    // 이렇게 하면 매번 동일한 기준으로 스케일이 계산됨
    const targetScale = center.zoom

    const currentHeight = window.innerHeight
    const calcHeight = currentHeight * 0.35

    // 화면에서의 목표 위치
    // - 가로: 화면 정중앙
    // - 세로: 화면 정중앙에서 300px 위
    const screenTargetX = stageWidth / 2
    const screenTargetY = calcHeight / 2 + 20

    // Konva 좌표 시스템:
    // 화면 좌표 = shape 좌표 * scale + stage 위치
    // 따라서 stage 위치 = 화면 좌표 - shape 좌표 * scale
    const targetX = screenTargetX - center.x * targetScale
    const targetY = screenTargetY - center.y * targetScale
    return await this.animatePositionForApp(targetX, targetY, targetScale)
  }

  protected async animatePosition(x: number, y: number, targetScale?: number): Promise<boolean> {
    if (Number.isNaN(x) || Number.isNaN(y)) return false
    const view = this.renderer.stage
    const currentX = view.x()
    const currentY = view.y()
    const currentScale = view.scaleX()
    // 타겟 스케일이 제공되지 않으면 현재 스케일 유지
    const finalTargetScale = targetScale || currentScale
    const distance = Math.sqrt(Math.pow(x - currentX, 2) + Math.pow(y - currentY, 2))
    const duration = Math.max(distance / (this.setPositionSpeed * 2.5), 0.2) // 2.5배 빠르게, 최소 0.2초

    // 애니메이션 객체 생성 및 시작
    return new Promise((resolve: (value: boolean | PromiseLike<boolean>) => void) => {
      const animation = this.renderer.ticker((frame: TickerFrame | undefined) => {
        const endAnimation = (value: boolean): void => {
          animation.stop()
          this.isAnimating = false
          resolve(value)
        }
        if (!frame) {
          return endAnimation(false)
        }

        const progress = frame.time / (duration * 1000)

        const easedProgress = easeInOut(progress)
        if (easedProgress < 0) {
          return endAnimation(false)
        }
        const currentScaleValue =
          currentScale * (1 - easedProgress) + finalTargetScale * easedProgress

        view.position({
          x: currentX * (1 - easedProgress) + x * easedProgress,
          y: currentY * (1 - easedProgress) + y * easedProgress,
        })
        view.scale({ x: currentScaleValue, y: currentScaleValue })

        this.renderer.requestDraw()

        // 애니메이션 완료 시 중지
        if (progress >= 1 || easedProgress >= 1) {
          view.position({ x, y })
          view.scale({ x: finalTargetScale, y: finalTargetScale })
          endAnimation(true)
        }
      })

      animation.start()
    })
  }
  protected async animatePositionForApp(
    x: number,
    y: number,
    targetScale?: number,
  ): Promise<boolean> {
    if (Number.isNaN(x) || Number.isNaN(y)) return false

    // 진행 중인 애니메이션이 있으면 즉시 중단
    if (this.currentAnimation) {
      const prevAnimation = this.currentAnimation
      prevAnimation.stop()
      this.currentAnimation = null
      if (this.currentAnimationResolve) {
        this.currentAnimationResolve(false)
        this.currentAnimationResolve = null
      }
      this.isAnimating = false
    }

    const view = this.renderer.stage
    const currentX = view.x()
    const currentY = view.y()
    const currentScale = view.scaleX()

    // 타겟 스케일이 제공되지 않으면 현재 스케일 유지
    const finalTargetScale = targetScale || currentScale

    const distance = Math.sqrt(Math.pow(x - currentX, 2) + Math.pow(y - currentY, 2))
    const duration = Math.max(distance / (this.setPositionSpeed * 2.5), 0.3) // 2.5배 빠르게, 최소 0.3초

    // 애니메이션 시작 플래그 설정
    this.isAnimating = true

    // 애니메이션 객체 생성 및 시작
    return new Promise((resolve: (value: boolean | PromiseLike<boolean>) => void) => {
      this.currentAnimationResolve = resolve
      let resolved = false
      let safetyTimeout: ReturnType<typeof setTimeout> | null = null

      const animation = this.renderer.ticker((frame: TickerFrame | undefined) => {
        const endAnimation = (value: boolean): void => {
          if (resolved) return
          resolved = true
          if (safetyTimeout) clearTimeout(safetyTimeout)
          animation.stop()
          this.currentAnimation = null
          this.currentAnimationResolve = null
          this.isAnimating = false
          resolve(value)
        }
        if (!frame) {
          return endAnimation(false)
        }

        const progress = frame.time / (duration * 1000)

        const easedProgress = easeInOut(progress)
        if (easedProgress < 0) {
          return endAnimation(false)
        }

        // 위치와 스케일을 동시에 애니메이션
        const currentPosX = currentX * (1 - easedProgress) + x * easedProgress
        const currentPosY = currentY * (1 - easedProgress) + y * easedProgress
        const currentScaleValue =
          currentScale * (1 - easedProgress) + finalTargetScale * easedProgress

        view.position({ x: currentPosX, y: currentPosY })
        view.scale({ x: currentScaleValue, y: currentScaleValue })

        this.renderer.requestDraw()

        // 애니메이션 완료 시 중지
        if (progress >= 1 || easedProgress >= 1) {
          view.position({ x, y })
          view.scale({ x: finalTargetScale, y: finalTargetScale })
          endAnimation(true)
        }
      })

      // 현재 애니메이션 저장
      this.currentAnimation = animation

      // 안전장치: 5초 후 강제 완료 (RAF 중단 대응)
      // animation 인스턴스를 직접 비교하여 다른 애니메이션에 영향 없도록 격리
      safetyTimeout = setTimeout(() => {
        if (!resolved && this.currentAnimation === animation) {
          resolved = true
          view.position({ x, y })
          view.scale({ x: finalTargetScale, y: finalTargetScale })
          animation.stop()
          this.currentAnimation = null
          this.currentAnimationResolve = null
          this.isAnimating = false
          resolve(true)
        }
      }, 5000)

      animation.start()
    })
  }

  // 하위 클래스(GrabController)에서 구현
  ctrlSelectEntity(_entity: CanvasEngineEntity): void {}

  pushToEntities(shape: CanvasEngineEntity): void {
    this.entities.push(shape)
  }

  getEntityInteractionMode(): boolean {
    return this.__entityInteractionMode
  }

  setEntityInteractionMode(mode: boolean): void {
    this.__entityInteractionMode = mode
  }

  // entity들 비활성화 (투명도 0.3, 선택 불가)
  setDisabledShapes(shapeIds: string[]): void {
    this.disabledEntityIds = new Set(shapeIds)
    for (const shapeId of shapeIds) {
      const entity = this.getEntityById(shapeId)
      if (!entity) continue

      // 끄기 전에 원래 상태를 노드에 백업 (복구 시 일괄 true로 되돌리면
      // 의도적으로 listening:false인 노드(연결선·풍선 텍스트 등)가 되살아나 클릭을 가로채고,
      // draggable은 생성 시점에만 켜지므로 영영 복구되지 않는다)
      // find는 '*' 선택자를 지원하지 않아(Konva는 빈 배열) 함수 선택자로 전체 순회
      const descendants = this.entityNode(entity).find(() => true)
      for (const node of descendants) {
        if (node.getAttr(DISABLED_STATE_BACKUP_ATTR) !== undefined) continue
        node.setAttr(DISABLED_STATE_BACKUP_ATTR, {
          listening: node.listening(),
          draggable: node.draggable(),
        })
      }

      entity.node.opacity(0.3)
      entity.node.listening(false)
      entity.node.draggable(false)

      // avaliableShape 비활성화
      if (entity.avaliableShape) {
        entity.avaliableShape.listening(false)
        entity.avaliableShape.draggable(false)
      }

      // balloon 비활성화
      if (entity.balloon) {
        entity.balloon.head.rect.listening(false)
        entity.balloon.head.rect.draggable(false)
        entity.balloon.head.text.listening(false)
        entity.balloon.head.text.draggable(false)
      }

      // 모든 자식 노드도 listening 및 draggable false 설정
      for (const node of descendants) {
        node.listening(false)
        node.draggable(false)
      }
    }
    this.renderer.layers.background.batchDraw()
  }

  // 모든 entity 비활성화 상태 해제 및 투명도 복구
  clearDisabledShapes(): void {
    // setDisabledShapes와 대칭으로 하위 노드를 복구하기 위해 비활성화됐던 ID를 보관
    const previouslyDisabled = new Set(this.disabledEntityIds)
    this.disabledEntityIds.clear()

    for (const entity of this.entities) {
      entity.node.opacity(1)
      entity.node.listening(true)

      // 비활성화됐던 엔티티만 하위 노드 listening 복구
      // (BRUSH는 avaliableShape=__brush Group의 listening이 꺼진 채 복구되지 않으면
      //  하위 Line의 hit이 막혀 선택 불가 — '*'는 무효 선택자라 함수 선택자로 전체 순회)
      if (previouslyDisabled.has(entity.getId())) {
        for (const node of this.entityNode(entity).find(() => true)) {
          const backup = node.getAttr(DISABLED_STATE_BACKUP_ATTR) as DisabledStateBackup | undefined
          if (!backup) continue
          node.listening(backup.listening)
          node.draggable(backup.draggable)
          node.setAttr(DISABLED_STATE_BACKUP_ATTR, undefined)
        }
        // BRUSH의 avaliableShape(__brush Group)는 재생성 등으로 백업이 없어도
        // listening이 꺼진 채 남으면 하위 Line의 hit이 막혀 선택 불가가 된다
        if (entity.avaliableShape) entity.avaliableShape.listening(true)

        // listening(false) 상태에서 구워진 hit 캐시는 stale(빈 hit 영역)이므로 폐기.
        // (종료 직전 applyMetaData가 비활성화 상태로 재캐싱하면 listening만 복구해도
        //  새로고침 전까지 선택 불가. uncached가 되면 hitFunc로 live hit하므로 선택 정상이고,
        //  재캐싱은 기존 캐시 시스템이 배치로 처리하므로 여기서 동기 cache()는 하지 않는다)
        if (this.renderer.hints.isStatic(entity.node)) this.renderer.hints.unmarkStatic(entity.node)
      }
    }
    this.renderer.layers.background.batchDraw()
  }

  // 색상필터 등으로 특정 엔티티를 숨김 (클릭/grab 선택 모두 차단)
  setHiddenEntityIds(ids: Set<string>): void {
    // 이전에 숨겨졌다가 이제 보여야 하는 엔티티 복원
    for (const prevId of this.hiddenEntityIds) {
      if (ids.has(prevId)) continue
      const entity = this.getEntityById(prevId)
      if (!entity) continue
      entity.node.show()
    }
    // 새로 숨겨야 하는 엔티티 처리
    for (const id of ids) {
      const entity = this.getEntityById(id)
      if (!entity) continue
      entity.node.hide()
    }
    this.hiddenEntityIds = new Set(ids)
    this.renderer.layers.background.batchDraw()
  }

  clearHiddenEntityIds(): void {
    for (const id of this.hiddenEntityIds) {
      const entity = this.getEntityById(id)
      if (!entity) continue
      entity.node.show()
    }
    this.hiddenEntityIds.clear()
    this.renderer.layers.background.batchDraw()
  }
}
